import React, { useEffect, useState, useMemo } from 'react';
import Select, { components } from 'react-select';
import type { OptionProps, SingleValueProps } from 'react-select';
import { useMutation, useQuery } from '@apollo/client';
import { generateSalesMaterial, getBookletsQuery } from '../../queries/booklet';
import { getAssociationsQuery } from '../../queries/association';
import { Booklet, PosterType } from '../../types/generated.types';
import { useNavigationContext } from '../../context/NavigationContext';
import { BsFilePdfFill } from 'react-icons/bs';
import { Button } from '../../common';
import { useHistory } from 'react-router-dom';
import { MdDownload } from 'react-icons/md';

const { REACT_APP_IMAGE_BASE_URL } = process.env;

enum MediaType {
  SOCIAL_MEDIA = 'socialMedia',
  POSTER = 'poster',
  CHECKOUT_SIGN = 'checkoutSign',
  ROLLUP = 'rollup',
  SCHOOL_CLASS_POSTER = 'schoolClassPoster',
  SCHOOL_CLASS_SOCIAL_MEDIA = 'schoolClassSocialMedia',
  ASSOCIATION_POSTER = 'associationPoster',
}
interface GeneratedMedia {
  socialMedia: string[];
  poster: string[];
  checkoutSign: string[];
  rollup: string[];
  schoolClassPoster: string[];
  schoolClassSocialMedia: string[];
  associationPoster: string[];
}

interface SelectOptionBase {
  value: string;
  label: string;
  subtitle?: string | null;
}

type BookletOption = SelectOptionBase;
type AssociationOption = SelectOptionBase;

type AssociationSource = {
  _id: string;
  name: string;
  zoneName?: string | null;
};

const SelectOptionDisplay: React.FC<{ title: string; subtitle?: string | null; highlighted?: boolean }> = ({ title, subtitle, highlighted }) => {
  const subtitleClassName = highlighted
    ? 'media-generate__select-option-subtitle media-generate__select-option-subtitle--highlighted'
    : 'media-generate__select-option-subtitle';

  return (
    <div className='media-generate__select-option'>
      <span className='media-generate__select-option-title'>{title}</span>
      {subtitle && <small className={subtitleClassName}>{subtitle}</small>}
    </div>
  );
};

const createSelectComponents = <OptionType extends SelectOptionBase>() => ({
  Option: (props: OptionProps<OptionType, false>) => (
    <components.Option {...props}>
      <SelectOptionDisplay
        title={props.data.label}
        subtitle={props.data.subtitle}
        highlighted={props.isSelected}
      />
    </components.Option>
  ),
  SingleValue: (props: SingleValueProps<OptionType, false>) => (
    <components.SingleValue {...props}>
      <SelectOptionDisplay title={props.data.label} subtitle={props.data.subtitle} />
    </components.SingleValue>
  ),
});

const bookletSelectComponents = createSelectComponents<BookletOption>();
const associationSelectComponents = createSelectComponents<AssociationOption>();

const posterTypeOptions = [
  { value: PosterType.ClassTrip, label: 'Klassresa' },
  { value: PosterType.Graduation, label: 'Studenten' },
  { value: PosterType.Team, label: 'Laget' },
  { value: PosterType.Association, label: 'Föreningen' },
];

const lagSkolaffischOptions = [
  { value: PosterType.ClassTrip, label: 'Klassresa' },
  { value: PosterType.Graduation, label: 'Studenten' },
  { value: PosterType.Team, label: 'Laget' },
];

interface MediaGenerateProps {
  match: {
    params: {
      association?: string;
      booklet?: string;
    };
  };
}

const MediaGenerate: React.FC<MediaGenerateProps> = ({ match }) => {
  const history = useHistory();
  const associationParam = match.params.association;
  const bookletParam = match.params.booklet;
  const { data: bookletsData, error: bookletsError } = useQuery(getBookletsQuery);
  const { data: associationsData, error: associationsError } = useQuery(getAssociationsQuery);

  const [generateSalesMaterialContent, { loading: isMediaGenerating }] = useMutation(generateSalesMaterial);

  const [generatedMedia, setGeneratedMedia] = useState<GeneratedMedia>({ socialMedia: [], poster: [], checkoutSign: [], rollup: [], schoolClassPoster: [], schoolClassSocialMedia: [], associationPoster: [] });
  const [selectedBooklet, setSelectedBooklet] = useState<Booklet | null>(null);
  const [selectedAssociation, setSelectedAssociation] = useState<AssociationOption | null>(null);
  const [type, setType] = useState<MediaType>(MediaType.SOCIAL_MEDIA);
  const [posterType, setPosterType] = useState<PosterType>(PosterType.ClassTrip);

  const { setNavigationActivated } = useNavigationContext();
  useEffect(() => {
    setNavigationActivated(false);
  }, []);

  // Filter booklets based on selected association
  const filteredBooklets = useMemo(() => {
    return bookletsData?.booklets?.filter((booklet: Booklet) => {
      if (!selectedAssociation) return false;
      return booklet.purchasableThroughAssociations?.includes(selectedAssociation.value);
    }) || [];
  }, [bookletsData?.booklets, selectedAssociation]);

  // Handle association parameter from URL
  useEffect(() => {
    if (associationParam && associationsData?.associations) {
      const foundAssociation = associationsData.associations.find((association: AssociationSource) => association._id === associationParam);
      if (foundAssociation) {
        setSelectedAssociation({
          value: foundAssociation._id,
          label: foundAssociation.name,
          subtitle: foundAssociation.zoneName,
        });
      }
    }
  }, [associationParam, associationsData]);

  useEffect(() => {
    if (bookletParam && filteredBooklets.length > 0) {
      const found = filteredBooklets.find((b: Booklet) => b._id === bookletParam);
      setSelectedBooklet(found);
    }
  }, [bookletParam, filteredBooklets]);

  useEffect(() => {
    if (selectedBooklet && selectedAssociation) {

      let posterTypeToSend = posterType;
      if (type === MediaType.SCHOOL_CLASS_POSTER) {
        const allowedTypes = [PosterType.ClassTrip, PosterType.Graduation, PosterType.Team];
        if (!allowedTypes.includes(posterTypeToSend)) {
          posterTypeToSend = PosterType.ClassTrip; // Default to ClassTrip if invalid
        }
      }

      generateSalesMaterialContent({
        variables: {
          bookletId: selectedBooklet._id,
          associationId: selectedAssociation.value,
          type,
          ...((type === MediaType.SCHOOL_CLASS_SOCIAL_MEDIA || type === MediaType.SCHOOL_CLASS_POSTER) && { posterType: posterTypeToSend }),
        }
      }).then(response => {
        const base64Images = response.data.generateSalesMaterial;
        if (Array.isArray(base64Images)) {
          setGeneratedMedia({
            socialMedia: type === MediaType.SOCIAL_MEDIA ? base64Images : [],
            poster: type === MediaType.POSTER ? base64Images : [],
            checkoutSign: type === MediaType.CHECKOUT_SIGN ? base64Images : [],
            rollup: type === MediaType.ROLLUP ? base64Images : [],
            schoolClassPoster: type === MediaType.SCHOOL_CLASS_POSTER ? base64Images : [],
            schoolClassSocialMedia: type === MediaType.SCHOOL_CLASS_SOCIAL_MEDIA ? base64Images : [],
            associationPoster: type === MediaType.ASSOCIATION_POSTER ? base64Images : [],
          });
        }
      });
    }
  }, [selectedBooklet, selectedAssociation, type, posterType]);

  const handleAssociationChange = (selectedOption: AssociationOption | null) => {
    setSelectedAssociation(selectedOption);
    setSelectedBooklet(null);
    // Clear the URL parameter when association changes
    history.push('/media-generate');
  };

  const handleBookletChange = (selectedOption: BookletOption | null) => {
    if (!selectedOption) {
      setSelectedBooklet(null);
      return;
    }

    const booklet = filteredBooklets.find((b: Booklet) => b._id === selectedOption.value) || null;
    setSelectedBooklet(booklet);
    if (booklet && selectedAssociation) {
      history.push(`/media-generate/${selectedAssociation.value}/${booklet._id}`);
    }
  };

  if (bookletsError) return <p>Error fetching booklets: {bookletsError.message}</p>;
  if (associationsError) return <p>Error fetching associations: {associationsError.message}</p>;

  const associationOptions: AssociationOption[] = associationsData?.associations?.map((association: AssociationSource) => ({
    value: association._id,
    label: association.name,
    subtitle: association.zoneName,
  })) || [];

  const bookletOptions: BookletOption[] = filteredBooklets.map((booklet: Booklet) => ({
    value: booklet._id,
    label: booklet.title,
    subtitle: booklet.subtitle,
  }));

  const handleTypeChange = async (type: MediaType) => {
    setType(type);
    if (type === MediaType.SCHOOL_CLASS_POSTER) {
      // If current posterType is Association, change it to ClassTrip (first allowed option)
      if (posterType === PosterType.Association) {
        setPosterType(PosterType.ClassTrip);
      }
      // Also ensure posterType is one of the three allowed values
      const allowedTypes = [PosterType.ClassTrip, PosterType.Graduation, PosterType.Team];
      if (!allowedTypes.includes(posterType)) {
        setPosterType(PosterType.ClassTrip);
      }
    }
  };

  const handlePosterTypeChange = async (selectedOption: { value: PosterType; label: string }) => {
    setPosterType(selectedOption.value);
  };

  const handleDownload = async () => {
    if (!selectedBooklet || !selectedAssociation) return;
    switch (type) {
      case MediaType.POSTER: {
        const currentMedia = generatedMedia.poster[0];
        const pdfBase64 = `data:application/pdf;base64,${currentMedia}`;
        const mediaLink = document.createElement('a');
        mediaLink.href = pdfBase64;
        mediaLink.download = `${selectedBooklet?.title}-poster.pdf`;
        mediaLink.click();
        break;
      }
      case MediaType.SOCIAL_MEDIA: {
        const currentMedia = generatedMedia.socialMedia;
        currentMedia.forEach((media, index) => {
          const mediaBase64 = `data:image/png;base64,${media}`;
          const mediaLink = document.createElement('a');
          mediaLink.href = mediaBase64;
          mediaLink.download = `${selectedBooklet?.title}-${type}-${index + 1}.png`;
          mediaLink.click();
        });
        break;
      }
      case MediaType.ROLLUP: {
        const currentMedia = generatedMedia.rollup[0];
        const pdfBase64 = `data:application/pdf;base64,${currentMedia}`;
        const mediaLink = document.createElement('a');
        mediaLink.href = pdfBase64;
        mediaLink.download = `${selectedBooklet?.title}-rollup.pdf`;
        mediaLink.click();
        break;
      }
      case MediaType.SCHOOL_CLASS_POSTER: {
        // Download all three posters: lag, klassresa, student
        const posterTypes = [PosterType.Team, PosterType.ClassTrip, PosterType.Graduation];
        const posterLabels = ['lag', 'klassresa', 'student'];

        for (let i = 0; i < posterTypes.length; i++) {
          const currentPosterType = posterTypes[i];
          const label = posterLabels[i];

          // Generate the poster for this type
          try {
            const response = await generateSalesMaterialContent({
              variables: {
                bookletId: selectedBooklet?._id,
                associationId: selectedAssociation?.value,
                type: 'schoolClassPoster',
                posterType: currentPosterType,
              }
            });

            const base64Images = response.data.generateSalesMaterial;
            if (Array.isArray(base64Images) && base64Images[0]) {
              const pdfBase64 = `data:application/pdf;base64,${base64Images[0]}`;
              const mediaLink = document.createElement('a');
              mediaLink.href = pdfBase64;
              mediaLink.download = `${selectedBooklet?.title}-lag-skolaffisch-${label}.pdf`;
              document.body.appendChild(mediaLink);
              mediaLink.click();
              document.body.removeChild(mediaLink);
              // Small delay between downloads
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          } catch (error) {
            console.error(`Error generating poster for ${label}:`, error);
          }
        }
        break;
      }
      case MediaType.CHECKOUT_SIGN: {
        const currentMedia = generatedMedia.checkoutSign[0];
        const pdfBase64 = `data:application/pdf;base64,${currentMedia}`;
        const mediaLink = document.createElement('a');
        mediaLink.href = pdfBase64;
        mediaLink.download = `${selectedBooklet?.title}-checkout-sign.pdf`;
        mediaLink.click();
        break;
      }
      case MediaType.SCHOOL_CLASS_SOCIAL_MEDIA: {
        const currentMedia = generatedMedia.schoolClassSocialMedia;
        currentMedia.forEach((media, index) => {
          const mediaBase64 = `data:image/png;base64,${media}`;
          const mediaLink = document.createElement('a');
          mediaLink.href = mediaBase64;
          const types = ['square', 'portrait', 'landscape', 'web'];
          const posterTypeLabel = posterTypeOptions.find(option => option.value === posterType)?.label || 'klassresa';
          mediaLink.download = `${selectedBooklet?.title}-school-class-social-${posterTypeLabel.toLowerCase()}-${types[index]}.png`;
          mediaLink.click();
        });
        break;
      }
      case MediaType.ASSOCIATION_POSTER: {
        // Only download index 2 ("Stärk föreningens kassa")
        const associationPosterMedia = generatedMedia.associationPoster;
        if (associationPosterMedia.length >= 3) {
          const financePoster = associationPosterMedia[2]; // Index 2
          const pdfBase64 = `data:application/pdf;base64,${financePoster}`;
          const mediaLink = document.createElement('a');
          mediaLink.href = pdfBase64;
          mediaLink.download = `${selectedBooklet?.title}-föreningsaffisch.pdf`;
          mediaLink.click();
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  return (
    <div className='booklets-view media-generate'>
      <div className='media-generate__selectors'>
        <div className='media-generate__association-selector'>
          <Select
            options={associationOptions}
            value={selectedAssociation}
            onChange={handleAssociationChange}
            placeholder="Välj en förening/skola..."
            components={associationSelectComponents}
            styles={{
              control: (base) => ({
                ...base,
                width: '100%',
                minWidth: 320,
              }),
            }}
            isSearchable
          />
        </div>
        {selectedAssociation && (
          <div className='media-generate__booklet-selector'>
            <Select
              options={bookletOptions}
              value={selectedBooklet ? bookletOptions.find(option => option.value === selectedBooklet._id) || null : null}
              onChange={handleBookletChange}
              placeholder='Välj ett häfte...'
              components={bookletSelectComponents}
              styles={{
                control: (base) => ({
                  ...base,
                  width: '100%',
                  minWidth: 320,
                }),
              }}
              isSearchable
            />
          </div>
        )}
      </div>
      <div className='booklets container bg-white'>
        <div className={`booklets-header`} style={{
          '--booklet-color-left': selectedBooklet?.colorLeft || '#e0e0e0',
          '--booklet-color-right': selectedBooklet?.colorRight || '#f0f0f0',
          '--booklet-color-font': selectedBooklet?.colorFont || '#333'
        } as React.CSSProperties}>
          <div className='booklet-bg left' />
          <div className='booklet-bg right' />
          <div className='booklet-logo'>
            <img src={(REACT_APP_IMAGE_BASE_URL || '') + (selectedBooklet?.logoURL || '')} alt={selectedBooklet?.title} />
          </div>
          <h1 className='booklet-title'>{selectedBooklet?.title || 'Generera kundmaterial'}</h1>
          <p className='valid-to booklet-valid-to'>
            {selectedBooklet?.validTo
              ? `Giltigt t.o.m ${new Date(selectedBooklet.validTo).toLocaleDateString('sv-SE', { day: 'numeric', month: 'numeric' })}`
              : 'Välj ett häfte för att generera kundmaterial'
            }
          </p>
        </div>

        <div className='media-generate__header'>
          <div className='media-generate__type-buttons'>
            <button
              onClick={() => handleTypeChange(MediaType.SOCIAL_MEDIA)}
              className={`sm media-generate__type-button ${type === MediaType.SOCIAL_MEDIA ? 'active' : ''}`}>
              Sociala medier
            </button>
            <button
              onClick={() => handleTypeChange(MediaType.POSTER)}
              className={`sm media-generate__type-button ${type === MediaType.POSTER ? 'active' : ''}`}>
              Affisch
            </button>
            <button
              onClick={() => handleTypeChange(MediaType.CHECKOUT_SIGN)}
              className={`sm media-generate__type-button ${type === MediaType.CHECKOUT_SIGN ? 'active' : ''}`}>
              Kassaskylt
            </button>
            <button
              onClick={() => handleTypeChange(MediaType.ROLLUP)}
              className={`sm media-generate__type-button ${type === MediaType.ROLLUP ? 'active' : ''}`}>
              Rollup
            </button>
            <button
              onClick={() => handleTypeChange(MediaType.SCHOOL_CLASS_SOCIAL_MEDIA)}
              className={`sm media-generate__type-button ${type === MediaType.SCHOOL_CLASS_SOCIAL_MEDIA ? 'active' : ''}`}>
              ÅF Inlägg
            </button>
            <button
              onClick={() => handleTypeChange(MediaType.SCHOOL_CLASS_POSTER)}
              className={`sm media-generate__type-button ${type === MediaType.SCHOOL_CLASS_POSTER ? 'active' : ''}`}>
              Lag/skolaffisch
            </button>
            <button
              onClick={() => handleTypeChange(MediaType.ASSOCIATION_POSTER)}
              className={`sm media-generate__type-button ${type === MediaType.ASSOCIATION_POSTER ? 'active' : ''}`}>
              Föreningsaffisch
            </button>
          </div>
          <Button onClick={handleDownload} className='btn primary sm' loading={isMediaGenerating}>
            <div className="media-generate__download-button">
              <MdDownload />
              <span>Ladda ner</span>
            </div>
          </Button>
        </div>

        <div className='media-generate__preview-container'>
          {type === MediaType.SOCIAL_MEDIA && (
            <div className='image-preview media-generate__social-media-preview'>
              <div className='media-generate__social-media-left'>
                <img
                  src={isMediaGenerating ? 'data:image/png;base64,placeholder' : `data:image/png;base64,${generatedMedia.socialMedia[2]}`}
                  alt={isMediaGenerating ? 'Loading...' : 'Landscape'}
                  className='media-generate__social-media-image landscape'
                />
                <img
                  src={isMediaGenerating ? 'data:image/png;base64,placeholder' : `data:image/png;base64,${generatedMedia.socialMedia[0]}`}
                  alt={isMediaGenerating ? 'Loading...' : 'Square'}
                  className='media-generate__social-media-image square'
                />
              </div>
              <div className='media-generate__social-media-right'>
                <img
                  src={isMediaGenerating ? 'data:image/png;base64,placeholder' : `data:image/png;base64,${generatedMedia.socialMedia[1]}`}
                  alt={isMediaGenerating ? 'Loading...' : 'Portrait'}
                  className='media-generate__social-media-image portrait'
                />
              </div>
            </div>
          )}
          {type === MediaType.POSTER && (
            <div className='image-preview'>
              {isMediaGenerating ? (
                <div className='media-generate__loading-container poster'>
                  <p><BsFilePdfFill /> Loading...</p>
                </div>
              ) : (
                <embed
                  src={`data:application/pdf;base64,${generatedMedia.poster[0]}`}
                  type="application/pdf"
                  width="100%"
                  className='media-generate__pdf-embed poster'
                />
              )}
            </div>
          )}
          {type === MediaType.CHECKOUT_SIGN && (
            <div className='image-preview'>
              {isMediaGenerating ? (
                <div className='media-generate__loading-container checkout-sign'>
                  <p><BsFilePdfFill /> Loading...</p>
                </div>
              ) : (
                <embed
                  src={`data:application/pdf;base64,${generatedMedia.checkoutSign[0]}`}
                  type="application/pdf"
                  width="100%"
                  className='media-generate__pdf-embed checkout-sign'
                />
              )}
            </div>
          )}
          {type === MediaType.ROLLUP && (
            <div className='image-preview'>
              {isMediaGenerating ? (
                <div className='media-generate__loading-container rollup'>
                  <p><BsFilePdfFill /> Loading...</p>
                </div>
              ) : (
                <embed
                  src={`data:application/pdf;base64,${generatedMedia.rollup[0]}`}
                  type="application/pdf"
                  width="100%"
                  className='media-generate__pdf-embed rollup'
                />
              )}
            </div>
          )}
          {type === MediaType.SCHOOL_CLASS_POSTER && (
            <div className='image-preview'>
              {isMediaGenerating ? (
                <div className='media-generate__loading-container school-class-poster'>
                  <p><BsFilePdfFill /> Loading...</p>
                </div>
              ) : (
                <embed
                  src={`data:application/pdf;base64,${generatedMedia.schoolClassPoster[0]}`}
                  type="application/pdf"
                  width="100%"
                  className='media-generate__pdf-embed school-class-poster'
                />
              )}
            </div>
          )}
          {type === MediaType.SCHOOL_CLASS_SOCIAL_MEDIA && (
            <div className='image-preview media-generate__school-class-preview'>
              <div className='media-generate__school-class-web-container'>
                <img
                  src={isMediaGenerating ? 'data:image/png;base64,placeholder' : `data:image/png;base64,${generatedMedia.schoolClassSocialMedia[3]}`}
                  alt={isMediaGenerating ? 'Loading...' : 'Web'}
                  className='media-generate__school-class-web-image'
                />
              </div>
              <div className='media-generate__school-class-images-container'>
                <div className='media-generate__social-media-left'>
                  <img
                    src={isMediaGenerating ? 'data:image/png;base64,placeholder' : `data:image/png;base64,${generatedMedia.schoolClassSocialMedia[2]}`}
                    alt={isMediaGenerating ? 'Loading...' : 'Landscape'}
                    className='media-generate__social-media-image landscape'
                  />
                  <img
                    src={isMediaGenerating ? 'data:image/png;base64,placeholder' : `data:image/png;base64,${generatedMedia.schoolClassSocialMedia[0]}`}
                    alt={isMediaGenerating ? 'Loading...' : 'Square'}
                    className='media-generate__social-media-image'
                  />
                </div>
                <div className='media-generate__social-media-right'>
                  <img
                    src={isMediaGenerating ? 'data:image/png;base64,placeholder' : `data:image/png;base64,${generatedMedia.schoolClassSocialMedia[1]}`}
                    alt={isMediaGenerating ? 'Loading...' : 'Portrait'}
                    className='media-generate__social-media-image portrait'
                  />
                </div>
              </div>
            </div>
          )}
          {type === MediaType.ASSOCIATION_POSTER && (
            <div className='image-preview'>
              {isMediaGenerating ? (
                <div className='media-generate__loading-container school-class-poster'>
                  <p><BsFilePdfFill /> Loading...</p>
                </div>
              ) : (
                // Only show index 2 ("Stärk föreningens kassa")
                generatedMedia.associationPoster.length >= 3 ? (
                  <embed
                    src={`data:application/pdf;base64,${generatedMedia.associationPoster[2]}`}
                    type="application/pdf"
                    width="100%"
                    className='media-generate__pdf-embed school-class-poster media-generate__association-poster-embed'
                  />
                ) : null
              )}
            </div>
          )}
          {type === MediaType.SCHOOL_CLASS_POSTER && (
            <div className='media-generate__school-class-selector'>
              <Select
                options={lagSkolaffischOptions}
                value={lagSkolaffischOptions.find(option => option.value === posterType) || lagSkolaffischOptions[0]}
                onChange={handlePosterTypeChange}
                placeholder="Välj typ..."
                styles={{
                  control: (base) => ({
                    ...base,
                    width: '200px',
                  }),
                }}
              />
            </div>
          )}
          {type === MediaType.SCHOOL_CLASS_SOCIAL_MEDIA && (
            <div className='media-generate__school-class-selector'>
              <Select
                options={posterTypeOptions}
                value={posterTypeOptions.find(option => option.value === posterType)}
                onChange={handlePosterTypeChange}
                placeholder="Välj typ..."
                styles={{
                  control: (base) => ({
                    ...base,
                    width: '200px',
                  }),
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaGenerate;
